import React, { useState, useEffect } from "react";
import { Button } from "../components/Button.jsx";
import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import {
  getCompensationConfig,
  getPostConfirmPolishMode,
  getPostConfirmWriteupMode,
  getPreLobbyMode,
  getReflectionTiming,
  hasShareableGroupRationale,
  isBaseRateStaticSyntheticGroupCondition,
  isFourTaskPilotCondition,
  isStoryNogroupCondition,
  isStoryNogroupTemplateCondition,
  usesPostInitialConditionInstructions,
} from "../utils/conditionUtils.js";

export function Agreement({ next }) {
  const player = usePlayer();
  const game = useGame();
  const [agreed, setAgreed] = useState(false);

  // Detect story nogroup conditions for individual-framed consent
  const treatment = game?.get("treatment") || {};
  const isFourTaskPilot = isFourTaskPilotCondition(treatment);
  const isStoryNogroup = isStoryNogroupCondition(treatment);
  const isStoryNogroupTemplate = isStoryNogroupTemplateCondition(treatment);
  const isShareableGroupRationale = hasShareableGroupRationale(treatment);
  const isBaseRateStaticSyntheticGroup =
    isBaseRateStaticSyntheticGroupCondition(treatment);
  const compensation = getCompensationConfig(treatment);
  const formatCurrencyAmount = (amount) =>
    `$${Number.isInteger(amount) ? amount : amount.toFixed(1).replace(/\.0$/, "")}`;
  const potentialBonusRangeText = `${formatCurrencyAmount(
    Math.min(
      compensation.accuracyBonusAmount,
      compensation.mostConvincingBonusAmount
    )
  )}-${formatCurrencyAmount(
    compensation.accuracyBonusAmount + compensation.mostConvincingBonusAmount
  )}`;
  const postConfirmWriteupMode = getPostConfirmWriteupMode(treatment);
  const hasPostConfirmPolishStep = Boolean(getPostConfirmPolishMode(treatment));
  const hasPersuasiveWriteupStep = postConfirmWriteupMode === "convince-others";
  const usesTemplateFollowUp =
    isShareableGroupRationale && getPreLobbyMode(treatment) === "template";
  // The baseline arm runs no reflection activity, no confirm step, and no
  // rationale writeup. Its consent text has to list the steps it actually runs
  // rather than inheriting the Socratic wording by default.
  const usesNoFollowUp =
    isShareableGroupRationale && getPreLobbyMode(treatment) === "none";
  // The post-peer conditions run a different procedure from their pre-peer
  // siblings: no confirm step, no rationale writeup, and the peer review comes
  // before the follow-up activity rather than after it. The consent text has to
  // list the steps they actually run.
  const isPostPeerTiming =
    isBaseRateStaticSyntheticGroup &&
    getReflectionTiming(treatment) === "post-peer";
  // The askall pair drops the second estimate, discusses all three answers in
  // the follow-up activity, and restricts the final answer to a choice among
  // those answers, so its procedures sentence differs again.
  const postPeerAskAll =
    treatment?.postPeerAskAll === true ||
    treatment?.factors?.postPeerAskAll === true;
  // Arms that hold the condition-specific instructions until after the initial
  // estimate. Consent is the first screen a participant sees, so a purpose or
  // procedures sentence that names the assigned condition would defeat the
  // order the instruction page now follows. Both sentences state the union of
  // the three procedures under the "might involve" hedge the text already uses,
  // so they are identical in the three arms and false in none of them.
  const usesNeutralConsentText = usesPostInitialConditionInstructions(treatment);
  // The post-peer follow-up arms hold their instructions to the same point, so
  // their procedures sentence is neutral in the same way, listing the steps in
  // the post-peer order. The ask-all follow-up adds what the activity covers,
  // which is the same in its two arms.

  // Store Prolific ID (participantKey) from URL
  // This ensures it's available in empirica export
  useEffect(() => {
    if (player) {
      // Get participantKey from URL as primary source
      const urlParams = new URLSearchParams(window.location.search);
      const participantKeyFromUrl = urlParams.get("participantKey") || "";
      
      // Use player.id as fallback (should be the same as participantKey)
      const prolificId = participantKeyFromUrl || player.id || "";
      
      if (prolificId) {
        const existingProlificId = player.get("prolificId");
        if (!existingProlificId) {
          player.set("prolificId", prolificId);
          console.log("[AGREEMENT] Stored Prolific ID:", prolificId);
        }
      }
    }
  }, [player]);

  function handleNext() {
    if (!agreed) {
      return;
    }
    next();
  }

  return (
    <div className="mt-3 sm:mt-5 p-20">
      <h3 className="text-lg leading-6 font-medium text-gray-900">
        Participant Agreement
      </h3>
      <div className="mt-4 space-y-3 text-gray-800">
        {/* Replace the placeholder below with the full agreement text */}
        <div className="bg-white rounded-md shadow-sm border p-4">
          <div className="space-y-3">
            <p>
              <strong>Project name:</strong>{" "}
              {isFourTaskPilot
                ? "Four-Question Prediction Task"
                : isStoryNogroupTemplate
                ? "Prediction Reasoning Task"
                : isStoryNogroup
                ? "AI-Assisted Prediction Task"
                : "AI-Assisted Group Decision Making"}
            </p>

            <p>
              This research is being conducted by Eaman Jahani and Xiang Cheng at the University of
              Maryland, College Park. The purpose of this research project is to
              {isFourTaskPilot
                ? " understand how people answer a short set of prediction questions."
                : usesNeutralConsentText
                ? " understand how structured reasoning prompts, AI-assisted reasoning, and earlier participant responses affect individual decision making."
                : isBaseRateStaticSyntheticGroup && usesNoFollowUp
                ? " understand how earlier participant responses affect individual decision making."
                : isBaseRateStaticSyntheticGroup && usesTemplateFollowUp
                ? " understand how structured reasoning prompts and earlier participant responses affect individual decision making."
                : isBaseRateStaticSyntheticGroup
                ? " understand how AI-assisted reasoning and earlier participant responses affect individual decision making."
                : isShareableGroupRationale && usesTemplateFollowUp
                ? " understand how structured reasoning prompts affect group decision making."
                : isShareableGroupRationale
                ? " understand how AI-assisted reasoning affects group decision making."
                : isStoryNogroupTemplate
                ? " understand individual prediction and reasoning."
                : isStoryNogroup
                ? " understand how AI affects individual prediction and reasoning."
                : " understand how AI affects group decision making."}
            </p>

            <p>
              {isFourTaskPilot
                ? "The procedures involve answering four prediction questions on your own and then completing a short exit survey."
                : usesNeutralConsentText && isPostPeerTiming && postPeerAskAll
                ? "The procedures might involve making an estimate, reviewing two responses from earlier participants, giving your estimate again, completing a structured follow-up questionnaire or interacting with an AI assistant about your estimate and the two earlier responses, and making a final individual estimate."
                : usesNeutralConsentText && isPostPeerTiming
                ? "The procedures might involve making an estimate, reviewing two responses from earlier participants, giving your estimate again, completing a structured follow-up questionnaire or interacting with an AI assistant, and making a final individual estimate."
                : usesNeutralConsentText
                ? "The procedures might involve making an estimate, completing a structured follow-up questionnaire or interacting with an AI assistant, confirming a prediction, reviewing two responses from earlier participants, and making a final individual estimate."
                : isPostPeerTiming && postPeerAskAll && usesTemplateFollowUp
                ? "The procedures might involve making an estimate, reviewing two responses from earlier participants, completing a structured follow-up questionnaire about each of the three answers, and giving a final answer by choosing among those answers."
                : isPostPeerTiming && postPeerAskAll
                ? "The procedures might involve making an estimate, reviewing two responses from earlier participants, discussing each of the three answers with an AI assistant, and giving a final answer by choosing among those answers."
                : isPostPeerTiming && usesTemplateFollowUp
                ? "The procedures might involve making an estimate, reviewing two responses from earlier participants, giving your estimate again, completing a structured follow-up questionnaire, and making a final individual estimate."
                : isPostPeerTiming
                ? "The procedures might involve making an estimate, reviewing two responses from earlier participants, giving your estimate again, interacting with an AI assistant, and making a final individual estimate."
                : isBaseRateStaticSyntheticGroup && usesNoFollowUp
                ? "The procedures might involve making an estimate, reviewing two responses from earlier participants, and making a final individual estimate."
                : isBaseRateStaticSyntheticGroup && usesTemplateFollowUp && hasPostConfirmPolishStep
                ? "The procedures might involve completing a structured follow-up questionnaire, confirming a prediction, writing a rationale, reviewing an AI-polished version of that rationale, reviewing two responses from earlier participants, and making a final individual estimate."
                : isBaseRateStaticSyntheticGroup && usesTemplateFollowUp
                ? "The procedures might involve completing a structured follow-up questionnaire, confirming a prediction, writing a rationale, reviewing two responses from earlier participants, and making a final individual estimate."
                : isBaseRateStaticSyntheticGroup && hasPostConfirmPolishStep
                ? "The procedures might involve interacting with an AI assistant, confirming a prediction, writing a rationale, reviewing an AI-polished version of that rationale, reviewing two responses from earlier participants, and making a final individual estimate."
                : isBaseRateStaticSyntheticGroup
                ? "The procedures might involve interacting with an AI assistant, confirming a prediction, writing a rationale, reviewing two responses from earlier participants, and making a final individual estimate."
                : isShareableGroupRationale && usesTemplateFollowUp && hasPostConfirmPolishStep
                ? "The procedures might involve completing a structured follow-up questionnaire, confirming a prediction, writing a rationale that will be shown to group members, reviewing an AI-polished version of that rationale, and conversing with other participants about a prediction task."
                : isShareableGroupRationale && usesTemplateFollowUp
                ? "The procedures might involve completing a structured follow-up questionnaire, confirming a prediction, writing a rationale that will be shown to group members, and conversing with other participants about a prediction task."
                : isShareableGroupRationale && hasPostConfirmPolishStep
                ? "The procedures might involve interacting with an AI assistant, confirming a prediction, writing a rationale that will be shown to group members, reviewing an AI-polished version of that rationale, and conversing with other participants about a prediction task."
                : isShareableGroupRationale
                ? "The procedures might involve interacting with an AI assistant, confirming a prediction, writing a rationale that will be shown to group members, and conversing with other participants about a prediction task."
                : hasPersuasiveWriteupStep && isStoryNogroupTemplate
                ? "The procedures may involve completing a structured follow-up questionnaire, confirming a prediction, writing a detailed persuasive rationale of at least 50 words, and then continuing to the next part of the study."
                : hasPersuasiveWriteupStep && isStoryNogroup
                ? "The procedures may involve interacting with an AI assistant about a prediction task, confirming a prediction, writing a detailed persuasive rationale of at least 50 words, and then continuing to the next part of the study."
                : isStoryNogroupTemplate
                ? "The procedures may involve completing a structured follow-up questionnaire about a prediction task."
                : isStoryNogroup
                ? "The procedures may involve interacting with an AI assistant about a prediction task."
                : "The procedures might involve interacting with an AI agent and conversing with other participants (or AI) about a prediction task."}
            </p>

            {isFourTaskPilot && (
              <p>
                To preserve the integrity of the experiment, you should complete
                the questions on your own without AI systems, LLMs, or other
                outside help. Copying and pasting is disabled during the task,
                and submissions may be reviewed for likely prohibited tool use.
              </p>
            )}

            <p>
              There is minimal risk from participating in this research study.
            </p>

            <p>
              We hope that, in the future, other
              people might benefit from this study through improved understanding
              {isFourTaskPilot
                ? " of how people answer prediction questions."
                : isStoryNogroupTemplate
                ? " of individual prediction reasoning."
                : " of AI."}
            </p>

            <p>
              Any potential loss of confidentiality will be minimized by storing
              data in a password protected computer managed by researchers from
              University of Maryland, College Park.
            </p>

            <p>
              If we write a report or article about this research project, your
              identity will be protected to the maximum extent possible. Your
              information may be shared with representatives of the University of
              Maryland, College Park or governmental authorities if you or someone
              else is in danger or if we are required to do so by law.
            </p>

            <p>
              {isFourTaskPilot
                ? "You will receive a guaranteed $1.5 base payment. In addition, each correct answer earns a $0.5 bonus, up to a maximum $2 total bonus across the four questions."
                : isBaseRateStaticSyntheticGroup
                ? `You will receive a guaranteed ${compensation.basePaymentAmountText} base payment plus a potential ${compensation.accuracyBonusText} bonus if your final individual estimate is accurate.`
                : isStoryNogroup
                ? "You will receive a guaranteed $1.5 base payment plus a potential $2 bonus for accurate prediction."
                : isShareableGroupRationale
                ? `You will receive a guaranteed ${compensation.basePaymentAmountText} base payment plus a potential ${potentialBonusRangeText} bonus payment.`
                : "You will receive guaranteed $3 base payment plus a potential $1.5-$4 bonus payment."}
            </p>

            <p>
              Your participation in this research is completely voluntary. You may
              choose not to take part at all. If you decide to participate in this
              research, you may stop participating at any time. If you decide not
              to participate in this study or if you stop participating at any
              time, you will not be penalized; however, you will lose any benefits to which you
              otherwise qualify.
            </p>

            <div className="space-y-1">
              <p>
                If you decide to stop taking part in the study, if you have
                questions, concerns, or complaints, or if you need to report an
                injury related to the research, please contact the investigator:
              </p>
              <ul className="list-disc pl-6">
                <li>Eaman Jahani</li>
                <li>University of Maryland, College Park</li>
                <li>
                  <a href="mailto:eaman@umd.edu" className="text-blue-600 hover:text-blue-800">
                    eaman@umd.edu
                  </a>
                </li>
              </ul>
            </div>

            <div className="space-y-1">
              <p>
                If you have questions about your rights as a research participant or
                wish to report a research-related injury, please contact:
              </p>
              <ul className="list-disc pl-6">
                <li>University of Maryland College Park Institutional Review Board Office</li>
                <li>1204 Marie Mount Hall</li>
                <li>College Park, Maryland, 20742</li>
                <li>
                  E-mail: <a href="mailto:irb@umd.edu" className="text-blue-600 hover:text-blue-800">irb@umd.edu</a>
                </li>
                <li>Telephone: 301-405-0678</li>
              </ul>
            </div>

            <p>
              For more information regarding participant rights, please visit:
              {" "}
              <a
                href="https://research.umd.edu/research-resources/research-compliance/institutional-review-board-irb/research-participants"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 break-all"
              >
                https://research.umd.edu/research-resources/research-compliance/institutional-review-board-irb/research-participants
              </a>
            </p>

            <p>
              This research has been reviewed according to the University of
              Maryland, College Park IRB procedures for research involving human
              subjects.
            </p>
          </div>
        </div>
        <label className="flex items-start gap-2 select-none">
          <input
            type="checkbox"
            className="mt-1"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <span>I have read and agree to the terms above.</span>
        </label>
      </div>
      <div className="mt-6">
        <Button handleClick={handleNext} disabled={!agreed} autoFocus>
          <p>Next</p>
        </Button>
      </div>
    </div>
  );
}
